import { defaultSteps } from "./steps";
import type { PartnerSteps } from "./steps";
import type { Partner, PartnerBackend, PartnerConversation, PartnerDefinition } from "./types";

/**
 * The baseline: an ordinary Rista coupon partner with no offers leg, and a backend that does
 * nothing partner-specific for it.
 *
 * 🔴 **Every unregistered `metaId` gets this**, which is what makes a partner the merchant enables
 * tomorrow safe rather than a crash or a half-applied discount: a code the coupon check validates,
 * a discount written onto the bill, no partner round trip, no OTP, nothing to release.
 *
 * ⚠️ `isExternalLoyalty: false` is the load-bearing default. A partner not declared external never
 * reaches the partner apply and verify path, so an unknown `metaId` degrades to the plain coupon
 * flow rather than sending a request no partner will answer.
 */
const DEFAULT_DATA: PartnerDefinition = {
  metaId: "default",
  name: "Default",
  backend: {
    listsOffers: false,
    appliesOffers: false,
    applyOtp: "none",
    takesTypedCodes: false,
    releaseNeeds: [],
    exercisedAtSave: false,
  },

  isExternalLoyalty: false,
  requiresCouponUnblock: false,
  unblockRequiresReferenceAndPhone: false,

  offerCodeField: "discountCode",
  hasCouponModal: false,

  requiresOtpOnApply: false,
  honoursOfferOtpFlag: false,
  returnsItemLevelDiscounts: false,
  rollsBackUnappliedCoupon: false,
  sendsRequestId: false,

  // Only read once a partner is external and its apply asked for an OTP; the values below are the
  // offer-endpoint shape, which is the one a new partner is likeliest to speak.
  verifyChannel: "offer-verify",
  wrapsVerifyInLoyaltyInput: false,
  verifySuccessSignal: "message",
  stampsExternalAuthentication: true,
  referenceIdSource: "none",

  refreshesCustomerRecord: false,
  releasesOnBillChange: false,

  holdsPointsOnRequest: false,
  optsOut: false,
  refundsPoints: false,

  codeNeedsOtp: false,
  releasesCouponOnWrongOtp: false,
  resendsCodeOtp: false,
};

export const DEFAULT_PARTNER: Partner = { ...DEFAULT_DATA, steps: defaultSteps(DEFAULT_DATA) };

/** What a partner's folder declares: its name, the backend's facts, and only what differs. */
export interface PartnerDeclaration extends Partial<PartnerConversation> {
  name: string;
  backend: PartnerBackend;
  /**
   * The conversation steps this partner does differently (its calls, payloads or answers differ in
   * that part), given the partner's data. Every other step is the default.
   */
  steps?: (partner: PartnerDefinition) => Partial<PartnerSteps>;
}

/** A partner = the baseline's data + what its folder declares, and the default steps + its own. */
export const definePartner = (metaId: string, { steps, ...declaration }: PartnerDeclaration): Partner => {
  const data: PartnerDefinition = { ...DEFAULT_DATA, ...declaration, metaId };
  return { ...data, steps: { ...defaultSteps(data), ...steps?.(data) } };
};
